import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import RaisedButton from 'material-ui/RaisedButton';
import FlatButton from 'material-ui/FlatButton';
import Checkbox from 'material-ui/Checkbox';
import LinearProgress from 'material-ui/LinearProgress';
import {
    Table,
    TableBody,
    TableHeader,
    TableHeaderColumn,
    TableRow,
    TableRowColumn,
} from 'material-ui/Table';
import { deleteSuite, getSuite, listSuites } from 'services/testRuns';
import StartDialog from './StartDialog';
import { DocStatus, HoverTip } from './HoverTip';
import { pinnedGazetteer } from './metrics';
import './styles.scss';

function revisionLines(runVer, key) {
    return ['cargo', 'vessel'].flatMap((type) => {
        const slot = runVer && runVer[type];
        const revision = slot && slot[key] && slot[key].revision;
        return revision ? [{ type, revision: String(revision) }] : [];
    });
}

function GazetteerCell({ row }) {
    const gaz = pinnedGazetteer(row);
    if (!gaz) {
        return '—';
    }
    return (
        <HoverTip label={gaz}>
            <span>{gaz.slice(0, 12)}</span>
        </HoverTip>
    );
}

function revisionText(runVer, key) {
    const lines = revisionLines(runVer, key);
    if (lines.length === 0) {
        return '—';
    }
    const unique = [...new Set(lines.map(item => item.revision))];
    if (unique.length === 1) {
        return unique[0];
    }
    return lines.map(item => `${item.type} ${item.revision}`).join(' · ');
}

export default function ListPage({ router }) {
    const queryClient = useQueryClient();
    const [selected, setSelected] = useState([]);
    const [startOpen, setStartOpen] = useState(false);
    const [source, setSource] = useState(null);
    const [repeatingId, setRepeatingId] = useState('');
    const [deletingId, setDeletingId] = useState('');
    const [deleteError, setDeleteError] = useState('');
    const { data, error, isLoading } = useQuery('test-runs', listSuites, {
        refetchInterval: 5000,
    });
    const suites = Array.isArray(data) ? data : [];

    const completedSelected = useMemo(
        () => selected.filter((id) => {
            const row = suites.find((item) => item.id === id);
            return row && row.status === 'completed';
        }),
        [selected, suites],
    );

    const toggle = (id, status) => {
        setSelected((prev) => {
            if (prev.indexOf(id) >= 0) {
                return prev.filter((item) => item !== id);
            }
            if (status !== 'completed') {
                return prev;
            }
            if (prev.length >= 2) {
                return [prev[1], id];
            }
            return prev.concat(id);
        });
    };

    const openCompare = () => {
        if (completedSelected.length !== 2) {
            return;
        }
        const [a, b] = completedSelected;
        const left = suites.find((item) => item.id === a);
        const right = suites.find((item) => item.id === b);
        const earlier = new Date(left.createdAt) <= new Date(right.createdAt) ? a : b;
        const later = earlier === a ? b : a;
        router.push(`/bbr/runs/compare?from=${encodeURIComponent(earlier)}&to=${encodeURIComponent(later)}`);
    };

    const onRepeat = async (row) => {
        setDeleteError('');
        setRepeatingId(row.id);
        try {
            const suite = await getSuite(row.id);
            setSource(suite);
            setStartOpen(true);
        } catch (err) {
            setDeleteError(err.message || 'Failed to load run');
        } finally {
            setRepeatingId('');
        }
    };

    const onDelete = async (row) => {
        const running = row.status === 'running' || row.status === 'collecting' || row.status === 'starting' || row.status === 'pending';
        const message = running
            ? 'Stop this run, release its queues and Swarm workers, and delete it and its results?'
            : 'Delete this run and its recognition results?';
        if (!window.confirm(message)) {
            return;
        }
        setDeleteError('');
        setDeletingId(row.id);
        try {
            await deleteSuite(row.id);
            setSelected((prev) => prev.filter((id) => id !== row.id));
            queryClient.invalidateQueries('test-runs');
        } catch (err) {
            setDeleteError(err.message || 'Failed to delete run');
        } finally {
            setDeletingId('');
        }
    };

    return (
        <div className="test-runs-page">
            <div className="test-runs-toolbar">
                <h2>Recognition tests</h2>
                <div>
                    <RaisedButton
                        label="Compare"
                        disabled={completedSelected.length !== 2}
                        onClick={openCompare}
                        style={{ marginRight: 8 }}
                    />
                    <RaisedButton
                        primary
                        label="Start run"
                        onClick={() => {
                            setSource(null);
                            setStartOpen(true);
                        }}
                    />
                </div>
            </div>
            {error && <div className="test-runs-error">{error.message}</div>}
            {deleteError && <div className="test-runs-error">{deleteError}</div>}
            {isLoading && <LinearProgress />}
            <Table selectable={false}>
                <TableHeader displaySelectAll={false} adjustForCheckbox={false}>
                    <TableRow>
                        <TableHeaderColumn style={{ width: 48 }} />
                        <TableHeaderColumn>Started</TableHeaderColumn>
                        <TableHeaderColumn>Status</TableHeaderColumn>
                        <TableHeaderColumn>Progress</TableHeaderColumn>
                        <TableHeaderColumn>Reason</TableHeaderColumn>
                        <TableHeaderColumn>Cargo</TableHeaderColumn>
                        <TableHeaderColumn>Vessel</TableHeaderColumn>
                        <TableHeaderColumn>Model SHA</TableHeaderColumn>
                        <TableHeaderColumn>Gazetteer SHA</TableHeaderColumn>
                        <TableHeaderColumn className="test-runs-actions" />
                    </TableRow>
                </TableHeader>
                <TableBody displayRowCheckbox={false} showRowHover>
                    {suites.map((row) => {
                        const total = (row.progress && row.progress.total) || 0;
                        const done = (row.progress && row.progress.done) || 0;
                        const pct = total ? (done / total) * 100 : 0;
                        const running = row.status === 'running' || row.status === 'collecting' || row.status === 'starting';
                        return (
                            <TableRow
                                key={row.id}
                                className="test-runs-doc-row"
                                selectable={false}
                                onClick={() => router.push(`/bbr/runs/${row.id}`)}
                            >
                                <TableRowColumn style={{ width: 48 }}>
                                    <span onClick={(event) => event.stopPropagation()}>
                                        <Checkbox
                                            checked={selected.indexOf(row.id) >= 0}
                                            disabled={row.status !== 'completed'}
                                            onCheck={() => toggle(row.id, row.status)}
                                        />
                                    </span>
                                </TableRowColumn>
                                <TableRowColumn>{new Date(row.createdAt).toLocaleString()}</TableRowColumn>
                                <TableRowColumn>
                                    <DocStatus status={row.status} />
                                </TableRowColumn>
                                <TableRowColumn>
                                    {running ? (
                                        <div>
                                            <LinearProgress mode="determinate" value={pct} />
                                            <span>{done}/{total}</span>
                                        </div>
                                    ) : (
                                        <span>{done}/{total}</span>
                                    )}
                                </TableRowColumn>
                                <TableRowColumn>{row.reason || '—'}</TableRowColumn>
                                <TableRowColumn>
                                    <HoverTip label={row.cargoImageTag}>
                                        <span>{row.cargoImageTag || '—'}</span>
                                    </HoverTip>
                                </TableRowColumn>
                                <TableRowColumn>
                                    <HoverTip label={row.vesselImageTag}>
                                        <span>{row.vesselImageTag || '—'}</span>
                                    </HoverTip>
                                </TableRowColumn>
                                <TableRowColumn>{revisionText(row.runVer, 'model')}</TableRowColumn>
                                <TableRowColumn>
                                    <GazetteerCell row={row} />
                                </TableRowColumn>
                                <TableRowColumn className="test-runs-actions">
                                    <span onClick={(event) => event.stopPropagation()}>
                                        <FlatButton
                                            label={repeatingId === row.id ? 'Loading…' : 'Repeat'}
                                            disabled={Boolean(repeatingId) || Boolean(deletingId)}
                                            onClick={() => onRepeat(row)}
                                        />
                                    </span>
                                    <FlatButton
                                        label={deletingId === row.id ? 'Deleting…' : 'Delete'}
                                        disabled={Boolean(deletingId) || Boolean(repeatingId)}
                                        labelStyle={{ color: '#c62828' }}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            onDelete(row);
                                        }}
                                    />
                                </TableRowColumn>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
            <StartDialog
                open={startOpen}
                source={source}
                onClose={() => setStartOpen(false)}
                onStarted={(id) => {
                    setStartOpen(false);
                    router.push(`/bbr/runs/${id}`);
                }}
            />
        </div>
    );
}



// WEBPACK FOOTER //
// ./app/components/TestRuns/ListPage.jsx